import { apiClient } from '@/libs/apiClient';
import type {
  ApiAiStatus,
  ApiApplicationStage,
  ApiExtractionStatus,
} from '@/libs/api/pipelineService';
import type { BlobUploadTarget } from '@/libs/api/jobDescriptionService';

export type UploadCVFile = {
  id: string;
  original_file_name: string;
  content_type: string;
  file_size: number;
  checksum_sha256: string;
};

export type UploadCVBody = {
  jd_id: string;
  source: string;
  channel: string;
  files: UploadCVFile[];
};

export type UploadCVResponse = {
  jd_id: string;
  items: {
    resume_id: string;
    application_id: string;
    original_file_name: string;
    upload: BlobUploadTarget;
    ai_status: ApiAiStatus;
    extraction_status?: ApiExtractionStatus;
    current_stage: ApiApplicationStage | null;
  }[];
};

export type ResumesCandidatesStatus = 'PENDING' | 'SCORED' | 'FAILED' | 'DUPLICATED';

export type ListResumesCandidatesPayload = {
  mine_only?: boolean;
  jd_id?: string;
  status?: ResumesCandidatesStatus;
  search?: string;
  page: number;
  page_size: number;
};

export type ListResumesCandidatesResponseItem = {
  resume_id: string;
  application_id: string;
  cv_name: string;
  position: string;
  uploaded_at: string;
  candidate_name?: string | null;
  extraction_status: ApiExtractionStatus;
  extraction_failure_reason?: string | null;
  ai_status: ResumesCandidatesStatus;
  source?: string | null;
  channel?: string | null;
};

export type ListResumesCandidatesResponse = {
  items: ListResumesCandidatesResponseItem[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
  total_files: number;
};

export type getDownloadCVUrlResponse = {
  download_url: string;
  expires_at: string;
};

export const getUploadCv = (params: ListResumesCandidatesPayload) =>
  apiClient.get<ListResumesCandidatesResponse>('/resume-uploads', { params });

export const postUploadCV = (body: UploadCVBody) =>
  apiClient.post<UploadCVResponse>('/resume-uploads', body);

export const putUploadBlob = async (url: string, file: File) => {
  await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type,
    },
  });
};

export const getDownloadCVUrl = (resumeId: string) =>
  apiClient.post<getDownloadCVUrlResponse>(`/candidate-resumes/${resumeId}/download-url`);
